import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CaptainSubscriptionsService } from 'src/app/captain-subscriptions.service';
import { FilterService, SingleSearchableDropdown } from 'src/app/filter.service';
import { Pagination, Table, TableData, TableDataTypes } from 'src/app/shared/types';

@Component({
    selector: 'app-captain-subscriptions',
    templateUrl: './captain-subscriptions.component.html'
})
export class CaptainSubscriptionsComponent implements OnInit {
    @Input() captainId: string;
    @Output() toggleHeaders: EventEmitter<any> = new EventEmitter();
    @Input() captainDetailsToggleableHeaders: string[];
    captainSubscriptions: Table;
    dataLoaded = false;
    purchasedSubscriptions: any[] = [];
    filtersApplied: { subscriptionType?: string; } = {};
    subscriptionTypeFilters = ['CASHBACK', 'REGULAR', 'POSTPAID', 'MC'];

    constructor(
        public captainSubscriptionsService: CaptainSubscriptionsService,
        private filterService: FilterService,
    ) { }

    ngOnInit() {
        this.declareTable();
    }

    declareTable() {
        this.captainSubscriptions = new Table({
            toggleableHeaders: this.captainDetailsToggleableHeaders,
            headers: {
                id: 'Subscription ID',
                paymentStatusAndMode: 'Payment Status / Mode',
                activeTime: 'Start Time\n End Time',
                planType: 'Plan Type',
                planStatus: 'Plan Status',
                consumption: 'Consumption',
                cityAndServices: 'City\n Services',
                commissionSaved: 'Commission\n Saved',
            },
            pagination: new Pagination(0, 10, 30, [10, 15, 20], 0, true),
            selectedHeader: 6,
            defaultError: 'No Subscriptions found',
            appliedFilters: this.filtersApplied,
            filterComponents: [
                new SingleSearchableDropdown({
                    key: 'subscriptionType',
                    title: 'Subscription Type',
                    values: this.subscriptionTypeFilters,
                    default: '',
                    placeholder: 'Search subscription type',
                } as any)
            ],
            onFilter: () => {
                const subscriptionType = this.filterService.appliedFilters.subscriptionType;
                this.filtersApplied = {subscriptionType: subscriptionType || ''};
                this.captainSubscriptions.filterCount = this.filtersApplied.subscriptionType ? 1 : 0;
                this.captainSubscriptions.appliedFilters = this.filtersApplied;
                this.filterPurchasedSubscriptions();
            },
            onRefresh: () => {
                this.captainSubscriptions = null;
                this.ngOnInit();
            },
            data: []
        });
        this.getSubscriptions();
    }

    async getSubscriptions(pageNumber = 1, perPage = 10) {
        this.dataLoaded = false;
        this.captainSubscriptionsService.getCaptainSubscriptions(this.captainId, pageNumber, perPage)
            .then(({ subscriptions }) => {
                this.purchasedSubscriptions = subscriptions || [];
                this.filterPurchasedSubscriptions();
                this.dataLoaded = true;
            }).catch(err => {
                console.log(err);
                this.dataLoaded = true;
            });
    }

    private filterPurchasedSubscriptions() {
        const selectedFilter = ((this.filtersApplied && this.filtersApplied.subscriptionType) || '').toUpperCase();
        const filteredSubscriptions = this.purchasedSubscriptions.filter((subscription) => {
            if (!selectedFilter) {
                return true;
            }
            return (subscription.subscriptionType || '').toUpperCase() === selectedFilter;
        });
        this.captainSubscriptions.pagination.count = filteredSubscriptions.length;
        this.captainSubscriptions.data = filteredSubscriptions.map((subscription) => {
            const typeLabel = (subscription.subscriptionType || 'NA').toUpperCase();
            const validityLine = (subscription.progress || []).map(p => `${p.type} validity`).join(', ') || 'NA';
            const commissionLine = (subscription.rules || []).map(r => `${r.operator} ${r.value} ${r.type == "percent" ? "% commission" : "commission"}`).join('\n') || 'NA';
            const paymentStatus = subscription.paymentStatus || subscription.status || 'NA';
            const amount = subscription.amount != null && subscription.amount !== '' ? subscription.amount : 'NA';
            const statusAmountMode = `${paymentStatus} - ${amount} - ${subscription.paymentMode || 'NA'}`;
            const deductionTime = typeLabel === 'POSTPAID' && paymentStatus !== 'SUCCESS'
                ? '-'
                : this.formatDeductionTime(subscription.deductionTime);
            const paymentLines = [statusAmountMode, deductionTime];
            let paymentCellHtml = `${paymentLines[0]}<br>${paymentLines[1]}`;
            if (subscription.cashback) {
                const cashbackAmount = subscription.cashback.amount != null ? subscription.cashback.amount : 'NA';
                const cashbackSuccess = (subscription.cashback.creditStatus || '').toUpperCase() === 'SUCCESS';
                const cashbackClass = cashbackSuccess ? 'text-success' : 'text-danger';
                paymentLines.push(`cashback - ${cashbackAmount}`);
                paymentCellHtml += `<br><span class="${cashbackClass}">cashback - ${cashbackAmount}</span>`;
            }
            const paymentCell = this.createTableData(paymentCellHtml, undefined, undefined, false, paymentLines.join('\n'));
            paymentCell['isHtml'] = true;
            const planTypeClass = typeLabel === 'CASHBACK' ? 'whiteSpacePre text-success' : undefined;
            return {
                id: this.createTableData(subscription.id, 'text-overflow-unset'),
                paymentStatusAndMode: paymentCell,
                activeTime: this.createTableData(subscription.startTime && subscription.endTime ? this.dateString(subscription.startTime) + "\n" + this.dateString(subscription.endTime) : "NA"),
                planType: this.createTableData(
                    `${typeLabel}\n${validityLine}\n${commissionLine}`,
                    planTypeClass
                ),
                planStatus: subscription.planStatus,
                consumption: this.buildConsumptionCell(subscription),
                cityAndServices: this.createTableData(subscription.cityName + "\n" + subscription.serviceNames.join(', ')),
                commissionSaved: subscription.commissionSaved,
            };
        });
    }

    private buildConsumptionCell(subscription: any) {
        const paymentOk = subscription.status == 'SUCCESS';
        const progressLines = (subscription.progress || []).map((p: any) => this.formatProgressLine(p, paymentOk));
        const cashbackLines = (subscription.cashbackProgress || []).map((p: any) => {
            const line = this.formatProgressLine(p, paymentOk);
            if (line === 'NA') {
                return line;
            }
            const missed = Number(p.current) < Number(p.target);
            return `<span class="${missed ? 'text-danger' : 'text-success'}">${line}</span>`;
        });
        const html = [...progressLines, ...cashbackLines].join('<br>') || 'NA';
        const hover = html.replace(/<br>/g, '\n').replace(/<[^>]+>/g, '');
        const cell = this.createTableData(html, undefined, undefined, false, hover);
        cell['isHtml'] = true;
        return cell;
    }

    private formatProgressLine(p: any, paymentOk: boolean): string {
        if (!p || p.type == 'unlimited' || !paymentOk) {
            return 'NA';
        }
        return `${p.current} / ${p.target} ${p.type}` + (p.totalRides != null ? `<br>${p.droppedRides} / ${p.totalRides} rides` : '');
    }

    dateString(epoch: number): string {
        return new Date(epoch * 1000).toLocaleString();
    }

    private formatDeductionTime(value: any): string {
        if (value == null || value === '') {
            return 'NA';
        }
        const epoch = Number(value);
        if (!isNaN(epoch) && epoch > 0) {
            return this.dateString(epoch > 1e12 ? epoch / 1000 : epoch);
        }
        return String(value);
    }

    createTableData(value: string, className?: string, type?: TableDataTypes, flag = false, hoverData = null) {
        return new TableData({
            data: value,
            type: type ? type : TableDataTypes.DATA,
            flag: flag,
            hoverData: hoverData,
            className: className ? className + ' cursor-pointer' : 'cursor-pointer whiteSpacePre',
        });
    }

    changeInHeader(header: string) {
        this.toggleHeaders.emit(header);
    }

    onPaginationChange(event: { pageNo: number, perPage: number; }) {
        this.getSubscriptions((event.pageNo + 1), event.perPage);
    }
}
